import { faCircleInfo, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Tooltip } from "antd";
import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import { VALIDATION_TYPE_OPTIONS } from "../../constants/formEnums";
import type { Validation } from "../../types/formTypes";
import SingleSelectField from "./SingleSelectField";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const ValidationList = styled.div`
  margin-top: 6px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    margin-bottom: 8px;
  }
  .ant-collapse-header {
    align-items: center !important;
  }
  .ant-collapse-header button {
    width: auto !important;
    min-width: unset !important;
    flex: 0 0 auto !important;
    background: none !important;
    border: none !important;
    padding: 4px 8px !important;
  }
  .ant-collapse-content-box {
    padding: 12px 16px !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

const ValidationFieldsWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto !important;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 4px 8px !important;
  color: var(--color-primary-desat) !important;
  font-size: 0.85rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: auto !important;
  margin-left: auto;

  &:hover {
    color: #d32f2f !important;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto !important;
  margin-top: 12px;
  padding: 10px 40px !important;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 1px dashed var(--color-primary-desat) !important;
  background: transparent !important;
  color: var(--color-primary) !important;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary) !important;
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

export interface ValidationFieldProps {
  label: string;
  name: string;
  value: Validation[];
  onChange: (value: Validation[]) => void;
  required?: boolean;
  tooltip?: string;
  /** Label for each item in the list, e.g. "Validation" */
  itemLabel?: string;
  /** Label for the add button, e.g. "+ Add Validation" */
  addButtonLabel?: string;
}

const emptyValidation: Validation = {
  additionalType: undefined,
  similarityOfResults: "",
  url: "",
};

const ValidationField: React.FC<ValidationFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  itemLabel = "Validation",
  addButtonLabel = "+ Add Validation",
}) => {
  const initialItems =
    value.length > 0 ? value : required ? [emptyValidation] : [];
  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    initialItems.map((_, i) => String(i)),
  );

  const items = value.length > 0 ? value : required ? [emptyValidation] : [];

  const updateItem = (index: number, updates: Partial<Validation>) => {
    const next = [...items];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const addItem = () => {
    const next = [...items, { ...emptyValidation }];
    onChange(next);
    setActiveKeys((prev) => [...prev, String(next.length - 1)]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1 && required) return;
    const next = items.filter((_, i) => i !== index);
    onChange(next);
    setActiveKeys((prev) =>
      prev
        .filter((k) => k !== String(index))
        .map((k) =>
          parseInt(k, 10) > index ? String(parseInt(k, 10) - 1) : k,
        ),
    );
  };

  const collapseItems = items.map((item, index) => ({
    key: String(index),
    label: (
      <PanelHeader>
        <span>{itemLabel}</span>
        {(items.length > 1 || !required) && items.length >= 1 && (
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeItem(index);
            }}
            aria-label={`Remove ${itemLabel.toLowerCase()}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        )}
      </PanelHeader>
    ),
    children: (
      <ValidationFieldsWrapper>
        <SingleSelectField
          label="Validation Type"
          name={`${name}-${index}-additionalType`}
          value={item.additionalType}
          onChange={(v) => updateItem(index, { additionalType: v })}
          options={[...VALIDATION_TYPE_OPTIONS]}
          tooltip="Type of the validation."
        />
        <TextField
          label="Similarity of Results"
          name={`${name}-${index}-similarityOfResults`}
          value={item.similarityOfResults ?? ""}
          onChange={(v) => updateItem(index, { similarityOfResults: v })}
          tooltip="Factor on how close the validation was."
        />
        <TextField
          label="URL to Validation"
          name={`${name}-${index}-url`}
          value={item.url ?? ""}
          onChange={(v) => updateItem(index, { url: v })}
          tooltip="Link (URL) to further information on the validation."
        />
      </ValidationFieldsWrapper>
    ),
  }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <ValidationList>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={(keys) =>
            setActiveKeys(Array.isArray(keys) ? keys : [keys])
          }
          items={collapseItems}
        />
        <AddButton type="button" onClick={addItem}>
          {addButtonLabel}
        </AddButton>
      </ValidationList>
    </FieldWrapper>
  );
};

export default ValidationField;
